import { useMemo } from "react";
import { useSelector } from "react-redux";
import { buildQueriesFromWidgetsByCubeId } from "@ts/revstack/helpers";
import {
    RevstackSummaryGraphCard,
    RevstackSummaryTableCard,
    RevstackTrendsWidget,
    RevstackBrandwiseDistributionPieWidget,
    RevstackCategoryPerformanceBarWidget,
    PerformanceCardWidget,
    RevstackComparisionOverview,
} from "@components/widgets";
import RevstackPerformanceTable from "@/components/widgets/RevstackPerformanceTable";
import { map } from "underscore";
import {
    RevstackCASectionIds,
    RevstackDashboardModules,
    RevstackDashboardSubModules,
    WidgetType,
    WidgetDimensions,
    DASHBOARD_TYPES,
    IChannel,
    IRevstackLayoutFilters,
    TSelectedDateRange,
    IRevstackRelativeFilters,
    IRevstackChannel,
    IMainFiltersValue,
    IDrilldownFiltersValue,
    ITrendsFiltersValue,
    ISkuMainFiltersValue,
    IAdditionalFiltersValue,
    IDashboardConfigRootState,
} from "@ts/common-types";
import { ALL_OPTIONS } from "@/ts/constants";
import {
    w0WidgetFormatter,
    w1WidgetFormatter,
    w2WidgetFormatter,
    w3WidgetFormatter,
    w4WidgetFormatter,
    w5WidgetFormatter,
    w6WidgetFormatter,
    w7WidgetFormatter,
    w9WidgetFormatter
} from "@/ts/revstack/formatters";
import { flattenWidgetStructure, mapWidgetStructure } from "@/ts/revstack/renderers";
import { generateChannelConfig, generateDdTrendsModalConfig, generateDrilldownConfig } from "@/ts/revstack/config-generators";
import RevstackProductAnalysisBanner from "@/components/widgets/RevstackProductAnalysisBanner";

const widgetMaster = [
    {
        id: WidgetType.W0,
        dimensions: [
            WidgetDimensions.VALUE,
            WidgetDimensions.CHANGE,
            WidgetDimensions.SERIES,
        ],
        widget: RevstackProductAnalysisBanner,
        formatter: w0WidgetFormatter
    },
    {
        id: WidgetType.W1,
        dimensions: [
            WidgetDimensions.VALUE,
            WidgetDimensions.CHANGE,
            WidgetDimensions.SERIES,
        ],
        widget: RevstackSummaryGraphCard,
        formatter: w1WidgetFormatter
    },
    {
        id: WidgetType.W2,
        dimensions: [WidgetDimensions.VALUE, WidgetDimensions.CHANGE],
        show_warning_indicator: true,
        widget: RevstackSummaryTableCard,
        formatter: w2WidgetFormatter,
        status: {
            categories: {
                good: { id: "good", color: "#198754" },
                medium: { id: "medium", color: "#efbe10" },
                poor: { id: "poor", color: "#ff0000" },
            },
            type: "range",
        }
    },
    {
        id: WidgetType.W3,
        dimensions: [
            WidgetDimensions.TIME_DIMENSION_LABELS,
            WidgetDimensions.SERIES,
        ],
        widget: RevstackTrendsWidget,
        formatter: w3WidgetFormatter
    },
    {
        id: WidgetType.W4,
        dimensions: [
            WidgetDimensions.TIME_DIMENSION_LABELS,
            WidgetDimensions.SERIES,
        ],
        widget: RevstackCategoryPerformanceBarWidget,
        formatter: w4WidgetFormatter
    },
    {
        id: WidgetType.W5,
        dimensions: [
            WidgetDimensions.TIME_DIMENSION_LABELS,
            WidgetDimensions.SERIES,
        ],
        widget: RevstackBrandwiseDistributionPieWidget,
        formatter: w5WidgetFormatter
    },
    {
        id: WidgetType.W6,
        dimensions: [
            WidgetDimensions.TIME_DIMENSION_LABELS,
            WidgetDimensions.SERIES,
        ],
        widget: PerformanceCardWidget,
        formatter: w6WidgetFormatter
    },
    {
        id: WidgetType.W7,
        dimensions: [
            WidgetDimensions.TIME_DIMENSION_LABELS,
            WidgetDimensions.SERIES,
        ],
        widget: RevstackPerformanceTable,
        formatter: w7WidgetFormatter
    },
    {
        id: WidgetType.W9,
        dimensions: [WidgetDimensions.VALUE, WidgetDimensions.CHANGE],
        widget: RevstackComparisionOverview,
        formatter: w9WidgetFormatter
    },
];

const useGetDashboardConfig = (
    module: string,
    configType: DASHBOARD_TYPES = DASHBOARD_TYPES.BASE,
    propSubModule?: RevstackDashboardSubModules,
    sectionId?: RevstackCASectionIds,
    skipSessionStorage?: boolean,
) => {

    const revstackLayoutFilters = useSelector(
        (state: IDashboardConfigRootState) => state.revstackLayoutFilters.value
    );
    const selectedDateRange = useSelector(
        (state: IDashboardConfigRootState) => state.selectedDateRange.value
    );
    const revstackChannel = useSelector((state: IDashboardConfigRootState) => state.revstackChannel.value);
    const revstackChannelsList = useSelector((state: IDashboardConfigRootState) => state.revstackChannelsList.value);
    const mainFilters = useSelector((state: IDashboardConfigRootState) => state.mainFilters.value);
    const drilldownFilters = useSelector((state: IDashboardConfigRootState) => state.drilldownFilters.value);
    const trendsFilters = useSelector((state: IDashboardConfigRootState) => state.trendsFilters.value);
    const skuMainFilters = useSelector((state: IDashboardConfigRootState) => state.skuMainFilters.value);
    const additionalFilters = useSelector((state: IDashboardConfigRootState) => state.additionalFilters.value);
    const clientSubGroupId = useSelector((state: IDashboardConfigRootState) => state.clientSubGroupId.value);
    const reduxSubModule = useSelector((state: IDashboardConfigRootState) => state.revstackActiveTab.value);
    const section = sectionId || RevstackCASectionIds.SP;
    const currencySymbol = useSelector((state: IDashboardConfigRootState) => state.channelCurrencySymbol.value);
    const relativeFilters = useSelector((state: IDashboardConfigRootState) => state.relativeFilters.value);
    const selectedSku = relativeFilters.skus?.[0] || "";

    const subModule = useMemo(() => {
        return propSubModule ?? reduxSubModule;
    }, [propSubModule, reduxSubModule]);

    const drilldownTrendsModalConfigs: Record<
        string,
        Record<string, any>
    > = useMemo(() => {
        const priceType = revstackLayoutFilters.priceType.selected;
        const metricType = revstackLayoutFilters.metricType.selected;
        const filterToAppend = `_${metricType}${priceType ? `_${priceType}` : ""
            }`;
        const isAllSubcatSelected = drilldownFilters?.selected?.subcategory?.includes(ALL_OPTIONS.value) || false;

        return {
            //!Note[DND]: In future, we have to maintain a channel modules mapping for the below...
            [RevstackDashboardModules.CATEGORY_ANALYSIS]: generateDdTrendsModalConfig(
                revstackChannel[0],
                filterToAppend,
                metricType,
                isAllSubcatSelected,
                RevstackDashboardModules.CATEGORY_ANALYSIS,
                subModule
            ),
            [RevstackDashboardModules.NAVIGATOR]: generateDdTrendsModalConfig(
                revstackChannel,
                filterToAppend,
                metricType,
                isAllSubcatSelected,
                RevstackDashboardModules.NAVIGATOR,
                subModule
            ),
            [RevstackDashboardModules.PRODUCT_ANALYSIS]: generateDdTrendsModalConfig(
                revstackChannel,
                filterToAppend,
                metricType,
                isAllSubcatSelected,
                RevstackDashboardModules.PRODUCT_ANALYSIS,
                subModule
            ),
        };
    }, [revstackLayoutFilters, drilldownFilters, revstackChannel, generateDdTrendsModalConfig, subModule]);

    const drilldownConfigs: Record<
        string,
        Record<string, any>
    > = useMemo(() => {
        const priceType = revstackLayoutFilters.priceType.selected;
        const metricType = revstackLayoutFilters.metricType.selected;
        const filterToAppend = `_${metricType}${priceType ? `_${priceType}` : ""
            }`;
        const isAllSubcatSelected = drilldownFilters?.selected?.subcategory?.includes(ALL_OPTIONS.value) || false;

        return {
            //!Note[DND]: In future, we have to maintain a channel modules mapping for the below...
            [RevstackDashboardModules.CATEGORY_ANALYSIS]: generateDrilldownConfig(
                revstackChannel[0],
                filterToAppend,
                metricType,
                isAllSubcatSelected,
                RevstackDashboardModules.CATEGORY_ANALYSIS,
                subModule,
                currencySymbol
            ),
            [RevstackDashboardModules.NAVIGATOR]: generateDrilldownConfig(
                revstackChannel,
                filterToAppend,
                metricType,
                isAllSubcatSelected,
                RevstackDashboardModules.NAVIGATOR,
                subModule,
                currencySymbol
            ),
            [RevstackDashboardModules.PRODUCT_ANALYSIS]: generateDrilldownConfig(
                revstackChannel,
                filterToAppend,
                metricType,
                isAllSubcatSelected,
                RevstackDashboardModules.PRODUCT_ANALYSIS,
                subModule,
                currencySymbol
            ),
        };
    }, [revstackLayoutFilters, drilldownFilters, revstackChannel, generateDrilldownConfig, subModule, currencySymbol]);

    const dashBoardConfigs: Record<
        string,
        Record<string, any>
    > = useMemo(() => {
        const priceType = revstackLayoutFilters.priceType.selected;
        const metricType = revstackLayoutFilters.metricType.selected;
        const filterToAppend = `_${metricType}${priceType ? `_${priceType}` : ""
            }`;

        return {
            [RevstackDashboardModules.CATEGORY_ANALYSIS]: generateChannelConfig(
                revstackChannel[0],
                filterToAppend,
                metricType,
                RevstackDashboardModules.CATEGORY_ANALYSIS,
                subModule,
                currencySymbol,
                selectedSku,
                selectedDateRange
            ),
            [RevstackDashboardModules.PRODUCT_ANALYSIS]: generateChannelConfig(
                revstackChannel[0],
                filterToAppend,
                metricType,
                RevstackDashboardModules.PRODUCT_ANALYSIS,
                subModule,
                currencySymbol,
                selectedSku,
                selectedDateRange
            ),
            [RevstackDashboardModules.NAVIGATOR]: generateChannelConfig(
                revstackChannel,
                filterToAppend,
                metricType,
                RevstackDashboardModules.NAVIGATOR,
                subModule,
                currencySymbol,
                selectedSku,
                selectedDateRange
            ),
        };
    }, [revstackLayoutFilters, drilldownFilters, revstackChannel, revstackChannelsList, generateChannelConfig, subModule, currencySymbol, selectedSku, selectedDateRange]);

    const sectiondConfig = useMemo(() => {
        const currentConfig =
            configType === DASHBOARD_TYPES.DRILL_DOWN
                ? drilldownConfigs?.[module]?.[subModule]?.[section]
                : configType === DASHBOARD_TYPES.DRILL_DOWN_TRENDS_MODAL
                    ? drilldownTrendsModalConfigs?.[module]?.[subModule]?.[section]
                    : dashBoardConfigs?.[module]?.[subModule];

        if (!currentConfig) {
            return null;
        }

        const sectionKeys = Object.keys(currentConfig);
        const formattedConfig = map(sectionKeys, (key: string) => ({
            sectionKey: key,
            config: {
                sectionId: key, // Use section key as identifier instead of cubeId
                widgets: map(
                    flattenWidgetStructure(currentConfig[key].widgets),
                    ({
                        type,
                        identifier,
                        measures,
                        queryDimensions,
                        queryMetaInfo,
                        relativePeriodConfig,
                        excludeFilter,
                        isExcludeDimension,
                        cubeId,
                    }) => ({
                        ...(identifier && { identifier }),
                        ...(type && { type }),
                        ...(measures && { measures }),
                        ...(queryDimensions && { queryDimensions }),
                        ...(queryMetaInfo && { queryMetaInfo }),
                        ...(relativePeriodConfig && { relativePeriodConfig }),
                        ...(excludeFilter && { excludeFilter }),
                        ...(isExcludeDimension && { isExcludeDimension }),
                        ...(cubeId && { cubeId }),
                    })
                ),
            },
        }));

        // To be used for query builder to generate custom queries
        if (!skipSessionStorage) {
            sessionStorage.setItem(
                `${configType === DASHBOARD_TYPES.DRILL_DOWN_TRENDS_MODAL
                    ? "drilldowntrendsmodal"
                    : configType === DASHBOARD_TYPES.DRILL_DOWN
                        ? "drilldown"
                        : "dashboard"
                }-config`,
                JSON.stringify(formattedConfig)
            );
        }

        return currentConfig;
    }, [
        revstackChannel,
        module,
        subModule,
        revstackLayoutFilters,
        dashBoardConfigs,
        drilldownConfigs,
        configType,
        section
    ]);

    // Memoize the entire widget config calculation
    const widgetConfig = useMemo(() => {
        if (!sectiondConfig) return [];

        const data = Object.keys(sectiondConfig).map((key) => {
            const config = sectiondConfig[key];
            // Add widgets to the config by mapping widget types to actual widget components
            if (config.widgets) {
                // Use recursive mapping to handle any level of nesting
                config.widgets = mapWidgetStructure(config.widgets, widgetMaster);

                // Use the centralized function to build queries from widgets by cubeId
                config.cubeQueries = buildQueriesFromWidgetsByCubeId(config.widgets);
            }
            return config;
        });
        return data;
    }, [
        sectiondConfig,
        clientSubGroupId,
        mainFilters,
        drilldownFilters,
        trendsFilters,
        skuMainFilters,
        additionalFilters,
        revstackLayoutFilters,
        selectedDateRange,
    ]);

    return {
        widgetConfig,
        configSessionKey: `${configType === DASHBOARD_TYPES.DRILL_DOWN
                ? "drilldown"
                : configType === DASHBOARD_TYPES.DRILL_DOWN_TRENDS_MODAL
                    ? "drilldown-trends-modal"
                    : "dashboard"
            }-config`,
    };
};

export default useGetDashboardConfig;
